import { useEffect, useRef, useState } from "react";
import { formatTabLabel } from "../atlasButtonStyles";

export default function AtlasTabBar({
  tabs,
  activeTab,
  onTabChange,
  className = "",
}) {
  const items = tabs.map((tab) =>
    typeof tab === "string" ? { id: tab, label: tab } : tab,
  );

  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const updateFades = () => {
      setCanScrollLeft(el.scrollLeft > 1);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    updateFades();
    el.addEventListener("scroll", updateFades, { passive: true });

    const resizeObserver = new ResizeObserver(updateFades);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener("scroll", updateFades);
      resizeObserver.disconnect();
    };
    // items.length covers tabs being added/removed (e.g. permission-gated tabs)
  }, [items.length]);

  return (
    <div className="relative mb-6">
      <div
        ref={scrollRef}
        className={`flex gap-8 border-b border-border overflow-x-auto whitespace-nowrap ${className}`}
        role="tablist"
      >
        {items.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onTabChange(tab.id)}
              className={`relative shrink-0 pb-4 px-1 text-sm font-medium uppercase transition-colors ${
                isActive
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {formatTabLabel(tab.label ?? tab.id)}
              {isActive ? (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-sidebar-active" />
              ) : null}
            </button>
          );
        })}
      </div>
      {/* Scroll affordance: fades hint that more tabs exist off-screen, since the
          tab list is horizontally scrollable but gives no other visual cue. */}
      {canScrollLeft ? (
        <div className="pointer-events-none absolute left-0 top-0 bottom-2 w-8 bg-gradient-to-r from-white to-transparent" />
      ) : null}
      {canScrollRight ? (
        <div className="pointer-events-none absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-white to-transparent" />
      ) : null}
    </div>
  );
}
